/* eslint-disable tsdoc/syntax */
import { styled } from "@mui/material";

import CheckCircle from "@zenith/assets/icons/CheckCircle";
import Circle from "@zenith/assets/icons/Circle";
import Clock from "@zenith/assets/icons/Clock";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import { TierStepsResponseDTO } from "@zenith/services/user";

/**
 * Props for Stepper Icon Component.
 * @interface
 * @name StepperIconComponentProps
 * @kind interface
 * @exports
 */
export interface StepperIconComponentProps {
  /**
   * Standard HTML Attribute, for classes.
   * @property
   * @name className
   * @kind property
   * @memberof StepperIconComponentProps
   * @type {string}
   */
  className?: string; // Made optional
  /**
   * If `true`, shows the active step.
   * @default false
   * @property
   * @name active
   * @kind property
   * @memberof StepperIconComponentProps
   * @type {boolean}
   */
  active?: boolean; // Made optional
  /**
   * If `true`, the step has been completed.
   * @default false
   * @property
   * @name completed
   * @kind property
   * @memberof StepperIconComponentProps
   * @type {boolean | undefined}
   */
  completed?: boolean; // Made optional
  /**
   * (optional)
   * Step status.
   * @property
   * @name stepStatus
   * @kind property
   * @memberof StepperIconComponentProps
   * @type {TierStepsResponseDTO.stepStatus | undefined}
   */
  stepStatus?: TierStepsResponseDTO.stepStatus;
}

/**
 * The styled root component for the step icon.
 * @constant
 * @name StepIconRoot
 * @kind variable
 * @type {StyledComponent<MUIStyledCommonProps<Theme> & { ownerState: { active?: boolean; }; }, React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, {}>}
 */
const StepIconRoot = styled("div")<{ ownerState: { active?: boolean } }>(
  ({ ownerState }) => ({
    color: "#eaeaf0",
    display: "flex",
    height: 40,
    alignItems: "center",
    ...(ownerState.active && {
      color: "#784af4",
    }),
    "& .StepIcon-completedIcon": {
      color: "#6EBD7F",
      zIndex: 1,
      fontSize: 12,
      width: 24,
      height: 24,
      borderRadius: "50%",
      border: "2px solid #6EBD7F",
    },
    "& .StepIcon-circle": {
      width: 24,
      height: 24,
      borderRadius: "50%",
      border: "1px solid black",
    },
  })
);

/**
 * Stepper Icon Component.
 * @function
 * @name StepperIconComponent
 * @kind function
 * @param {StepperIconComponentProps} props Props for Stepper Icon Component.
 * @returns {JSX.Element} Stepper Icon Component.
 * @exports
 */
export function StepperIconComponent(
  props: StepperIconComponentProps
): JSX.Element {
  const { active, className, completed, stepStatus } = props;

  function getStepIcon() {
    switch (stepStatus) {
      case TierStepsResponseDTO.stepStatus.APPROVED:
        return <CheckCircle fill={"#6EBD7F"} />;
      case TierStepsResponseDTO.stepStatus.PENDING:
        return <Clock stroke={"#F0B02A"} />;
      case TierStepsResponseDTO.stepStatus.REJECTED:
        return <CloseCircle stroke={"#DA291C"} />;
      default: {
        return completed ? (
          <CheckCircle fill={"#6EBD7F"} />
        ) : (
          <Circle fill={"#9B9B9B"} />
        );
      }
    }
  }

  return (
    <StepIconRoot ownerState={{ active }} className={className}>
      {getStepIcon()}
    </StepIconRoot>
  );
}

export default StepperIconComponent;
